import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { AlertCircle, CheckCircle, Lock, Mail, ArrowLeft, RefreshCw, Calculator } from 'lucide-react';
import { toast } from 'sonner';
import api, { authAPI } from '../lib/api';

const ResetPassword = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');
  
  const [mode, setMode] = useState(token ? 'reset' : 'forgot'); // 'forgot' or 'reset'
  const [loading, setLoading] = useState(false);
  const [email, setEmail] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const [tokenValid, setTokenValid] = useState(null);
  const [tokenEmail, setTokenEmail] = useState('');
  
  // CAPTCHA state
  const [captcha, setCaptcha] = useState(null);
  const [captchaAnswer, setCaptchaAnswer] = useState('');
  const [loadingCaptcha, setLoadingCaptcha] = useState(false);

  const fetchCaptcha = useCallback(async () => {
    setLoadingCaptcha(true);
    setCaptchaAnswer('');
    try {
      const response = await authAPI.getCaptcha();
      setCaptcha(response.data);
    } catch (err) {
      console.error('Failed to load CAPTCHA:', err);
    } finally {
      setLoadingCaptcha(false);
    }
  }, []);

  // Verify token on mount if in reset mode
  useEffect(() => {
    if (token) {
      verifyToken();
    } else {
      // Load CAPTCHA for forgot password mode
      fetchCaptcha();
    }
  }, [token, fetchCaptcha]);

  const verifyToken = async () => {
    try {
      const response = await api.get(`/api/auth/verify-reset-token/${token}`);
      if (response.data.valid) {
        setTokenValid(true);
        setTokenEmail(response.data.email || '');
      } else {
        setTokenValid(false);
        setError(response.data.error || 'Invalid reset link');
      }
    } catch (err) {
      setTokenValid(false);
      setError('Invalid or expired reset link');
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setError('');
    
    if (!captchaAnswer.trim()) {
      setError('Please solve the security check');
      return;
    }
    
    setLoading(true);

    try {
      await api.post('/api/auth/forgot-password', { 
        email,
        captcha_challenge_id: captcha?.challenge_id,
        captcha_response: captchaAnswer.trim()
      });
      setSuccess(true);
      toast.success('Reset link sent! Check your email.');
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : 'Failed to send reset link');
      // Refresh CAPTCHA on failure
      fetchCaptcha();
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    setError('');

    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (newPassword.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }

    setLoading(true);

    try {
      await api.post('/api/auth/reset-password', {
        token,
        new_password: newPassword
      });
      setSuccess(true);
      toast.success('Password reset successfully!');
      setTimeout(() => navigate('/login'), 2000);
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : 'Failed to reset password');
    } finally {
      setLoading(false);
    }
  };

  // Forgot Password Form
  if (mode === 'forgot') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 to-slate-100 p-4">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <div className="mx-auto w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mb-4">
              <Mail className="w-6 h-6 text-blue-600" />
            </div>
            <CardTitle>Forgot Password?</CardTitle>
            <CardDescription>
              Enter your email address and we'll send you a link to reset your password
            </CardDescription>
          </CardHeader>
          <CardContent>
            {success ? (
              <div className="text-center space-y-4">
                <div className="mx-auto w-12 h-12 bg-emerald-100 rounded-full flex items-center justify-center">
                  <CheckCircle className="w-6 h-6 text-emerald-600" />
                </div>
                <h3 className="font-semibold text-lg">Check your email</h3>
                <p className="text-sm text-slate-500">
                  We've sent a password reset link to <strong>{email}</strong>
                </p>
                <p className="text-xs text-slate-400">
                  Didn't receive it? Check your spam folder or try again.
                </p>
                <Button variant="outline" onClick={() => setSuccess(false)} className="mt-4">
                  Send another link
                </Button>
              </div>
            ) : (
              <form onSubmit={handleForgotPassword} className="space-y-4">
                {error && (
                  <div className="p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2 text-red-700 text-sm">
                    <AlertCircle className="w-4 h-4 flex-shrink-0" />
                    {error}
                  </div>
                )}

                <div className="space-y-2">
                  <Label htmlFor="email">Email Address</Label>
                  <Input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="Enter your email"
                    required
                    data-testid="forgot-email-input"
                  />
                </div>

                {/* CAPTCHA Section */}
                <div className="space-y-2">
                  <Label className="flex items-center gap-2">
                    <Calculator className="w-4 h-4" />
                    Security Check
                  </Label>
                  <div className="p-3 bg-slate-50 border border-slate-200 rounded-lg">
                    {loadingCaptcha ? (
                      <div className="flex items-center justify-center py-2">
                        <div className="spinner w-4 h-4 border-slate-600 border-t-transparent"></div>
                        <span className="ml-2 text-sm text-slate-600">Loading...</span>
                      </div>
                    ) : (
                      <>
                        <div className="flex items-center justify-between mb-2">
                          <p className="text-sm font-medium text-slate-800" data-testid="forgot-captcha-question">
                            {captcha?.question || 'Loading...'}
                          </p>
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={fetchCaptcha}
                            className="text-slate-500 hover:text-slate-700 h-8 w-8 p-0"
                          >
                            <RefreshCw className="w-4 h-4" />
                          </Button>
                        </div>
                        <Input
                          type="text"
                          placeholder="Your answer"
                          value={captchaAnswer}
                          onChange={(e) => setCaptchaAnswer(e.target.value)}
                          className="h-9"
                          required
                          data-testid="forgot-captcha-input"
                        />
                      </>
                    )}
                  </div>
                </div>

                <Button type="submit" className="w-full" disabled={loading || loadingCaptcha} data-testid="forgot-submit-btn">
                  {loading ? 'Sending...' : 'Send Reset Link'}
                </Button>

                <div className="text-center">
                  <Link 
                    to="/login" 
                    className="text-sm text-blue-600 hover:underline inline-flex items-center gap-1"
                  >
                    <ArrowLeft className="w-4 h-4" />
                    Back to Login
                  </Link>
                </div>
              </form>
            )}
          </CardContent>
        </Card>
      </div>
    );
  }

  // Reset Password Form (with token)
  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 to-slate-100 p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="mx-auto w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mb-4">
            <Lock className="w-6 h-6 text-blue-600" />
          </div>
          <CardTitle>Reset Your Password</CardTitle>
          <CardDescription>
            {tokenEmail ? `Enter a new password for ${tokenEmail}` : 'Enter your new password'}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {tokenValid === false ? (
            <div className="text-center space-y-4">
              <div className="mx-auto w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
                <AlertCircle className="w-6 h-6 text-red-600" />
              </div>
              <h3 className="font-semibold text-lg text-red-700">Invalid Reset Link</h3>
              <p className="text-sm text-slate-500">{error}</p>
              <Button onClick={() => { setMode('forgot'); setError(''); }} className="mt-4">
                Request a new link
              </Button>
            </div>
          ) : success ? (
            <div className="text-center space-y-4">
              <div className="mx-auto w-12 h-12 bg-emerald-100 rounded-full flex items-center justify-center">
                <CheckCircle className="w-6 h-6 text-emerald-600" />
              </div>
              <h3 className="font-semibold text-lg">Password Reset!</h3>
              <p className="text-sm text-slate-500">
                Your password has been reset successfully. Redirecting to login...
              </p>
            </div>
          ) : tokenValid === null ? (
            <div className="text-center py-8">
              <div className="spinner w-8 h-8 mx-auto border-blue-600"></div>
              <p className="text-sm text-slate-500 mt-4">Verifying reset link...</p>
            </div>
          ) : (
            <form onSubmit={handleResetPassword} className="space-y-4">
              {error && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2 text-red-700 text-sm">
                  <AlertCircle className="w-4 h-4 flex-shrink-0" />
                  {error}
                </div>
              )}

              <div className="space-y-2">
                <Label htmlFor="new-password">New Password</Label>
                <Input
                  id="new-password"
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="Enter new password"
                  required
                  minLength={6}
                  data-testid="new-password-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="confirm-password">Confirm Password</Label>
                <Input
                  id="confirm-password"
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Confirm new password"
                  required
                  data-testid="confirm-password-input"
                />
              </div>

              <Button type="submit" className="w-full" disabled={loading} data-testid="reset-submit-btn">
                {loading ? 'Resetting...' : 'Reset Password'}
              </Button>

              <div className="text-center">
                <Link 
                  to="/login" 
                  className="text-sm text-blue-600 hover:underline inline-flex items-center gap-1"
                >
                  <ArrowLeft className="w-4 h-4" />
                  Back to Login
                </Link>
              </div>
            </form>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default ResetPassword;
